// src/components/SystemAccessPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import SignInHeader from './SigninHeader';
import Navigation from '../layout/Navigation';
import { ChevronRight } from 'lucide-react';

export default function SystemAccessPage() {
  const navigate = useNavigate();

  // -------------------------------------------
  // 1) Dark/Light Theme Handling
  // -------------------------------------------
  const [isDarkMode, setIsDarkMode] = useState(false);
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark =
      window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  // -------------------------------------------
  // 2) Logged‐In User Context for Header
  // -------------------------------------------
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');
  let userObj = null;
  if (storedUser) {
    try {
      userObj = JSON.parse(storedUser);
    } catch {
      userObj = null;
    }
  }
  const isLoggedIn = Boolean(token);

  // -------------------------------------------
  // 3) Fixed Three Environments’ State
  // -------------------------------------------
  const [environments, setEnvironments] = useState([
    { key: 'production', label: 'Production', url: '', username: '', password: '' },
    { key: 'test', label: 'Test', url: '', username: '', password: '' },
    { key: 'support', label: 'Support', url: '', username: '', password: '' },
  ]);

  const handleEnvChange = (index, field, value) => {
    setEnvironments((prev) => {
      const copy = [...prev];
      copy[index][field] = value;
      return copy;
    });
  };

  const handleTestConnection = async (envKey) => {
    const env = environments.find((e) => e.key === envKey);
    if (!env) return;

    try {
      const response = await fetch(`/api/credentials/${envKey}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: env.url,
          username: env.username,
          password: env.password,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        alert(`✅ ${env.label} connection successful!`);
      } else {
        alert(`❌ ${env.label} connection failed:\n${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert(`❌ ${env.label} connection error: ${err.message}`);
    }
  };

  const handleSaveAll = async () => {
    try {
      const toSave = environments.map(({ key, url, username, password }) => ({
        environment: key,
        url,
        username,
        password,
      }));
      const response = await fetch('/api/credentials/save_all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credentials: toSave }),
      });
      const data = await response.json();
      if (response.ok) {
        alert('✅ All credentials saved successfully!');
      } else {
        alert(`❌ Saving failed:\n${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert(`❌ Save error: ${err.message}`);
    }
  };

  // -------------------------------------------
  // 4) Render
  // -------------------------------------------
  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-main-dark text-gray-300' : 'bg-main-light text-gray-600'
      }`}
    >
      {/* ─────────────────────────────────────────────────────────────────────
          A) Top‐Level Header + Navigation
      ───────────────────────────────────────────────────────────────────── */}
      <SignInHeader
        user={userObj}
        isLoggedIn={isLoggedIn}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />
      <Navigation
        activeTab="Settings"
        setActiveTab={() => navigate('/mainscreen')}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
      />

      <main className="pt-30 pb-12 px-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-5" style={{ paddingTop: '20px' }}>
          {/* ─────────────────────────────────────────────────────────────────
              B) Breadcrumb (“Settings > System Access”), Settings clickable
          ───────────────────────────────────────────────────────────────── */}
          <nav className="text-sm mb-4 flex items-center space-x-1 text-gray-500">
            <span
              onClick={() => navigate('/mainscreen')}
              className="cursor-pointer hover:underline"
            >
              Settings
            </span>
            <ChevronRight className="w-4 h-4" />
            <span className="font-medium text-gray-700">System Access</span>
          </nav>

          {/* ─────────────────────────────────────────────────────────────────
              C) Page Heading + Description
          ───────────────────────────────────────────────────────────────── */}
          <h1 className="text-3xl font-bold mb-2">System Access</h1>
          <p className={`mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Provide secure system access for Botable to connect with your jurisdiction’s Accela
            environments. This ensures seamless integration and functionality across Production,
            Test, Support, and other environments.
          </p>

          {/* ─────────────────────────────────────────────────────────────────
              D) Setup Instructions Card
          ───────────────────────────────────────────────────────────────── */}
          <div
            className={`mb-8 p-6 rounded-lg shadow ${
              isDarkMode ? 'bg-[#2D2D30] border border-gray-700' : 'bg-white border border-gray-200'
            }`}
          >
            <h2 className="text-xl font-semibold mb-4">Setup Instructions</h2>
            <ul className="space-y-2 list-disc list-inside">
              <li>
                <strong>Create Dedicated Accounts:</strong> For each environment, create a
                Botable‐specific user account with full administrative access. Do not use personal
                or shared accounts.
              </li>
              <li>
                <strong>Enter Environment URLs:</strong> Provide the full URL of the back‐office
                login page for each environment (e.g., Production, Test, Support).
              </li>
              <li>
                <strong>Secure Credentials:</strong> Use strong passwords. Each username and
                password will be encrypted and stored securely.
              </li>
              <li>
                <strong>Test Connectivity:</strong> After saving, use the Test Connection button
                to verify each environment. Botable will automatically confirm access and
                functionality.
              </li>
              <li>
                <strong>Add Additional Environments:</strong> Contact your Botable administrator if
                you need more than the default three environments.
              </li>
            </ul>
          </div>

          {/* ─────────────────────────────────────────────────────────────────
              E) Credentials Cards (Production, Test, Support)
              • URL / Username / Password aligned in one row
              • “Test Connection” button on second row under URL
          ───────────────────────────────────────────────────────────────── */}
          <div className="space-y-8">
            {environments.map((env, index) => (
              <div
                key={env.key}
                className={`p-6 rounded-lg shadow ${
                  isDarkMode ? 'bg-[#2D2D30] border border-gray-700' : 'bg-white border border-gray-200'
                }`}
              >
                <h3 className="text-lg font-semibold mb-4">{env.label}</h3>

                {/* Grid Layout: 3 columns for URL / Username / Password */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 1) URL Field */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">URL</label>
                    <input
                      type="text"
                      placeholder={`https://aca-${env.key}.accela.com/your-jurisdiction`}
                      className={`w-full px-3 py-2 rounded border ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-600 text-gray-200'
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      } focus:outline-none`}
                      value={env.url}
                      onChange={(e) => handleEnvChange(index, 'url', e.target.value)}
                    />
                  </div>

                  {/* 2) Username Field */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">Username</label>
                    <input
                      type="text"
                      placeholder="botable_user"
                      className={`w-full px-3 py-2 rounded border ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-600 text-gray-200'
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      } focus:outline-none`}
                      value={env.username}
                      onChange={(e) => handleEnvChange(index, 'username', e.target.value)}
                    />
                  </div>

                  {/* 3) Password Field */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium mb-1">Password</label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      className={`w-full px-3 py-2 rounded border ${
                        isDarkMode
                          ? 'bg-gray-800 border-gray-600 text-gray-200'
                          : 'bg-gray-50 border-gray-300 text-gray-700'
                      } focus:outline-none`}
                      value={env.password}
                      onChange={(e) => handleEnvChange(index, 'password', e.target.value)}
                    />
                  </div>
                </div>

                {/* “Test Connection” button sits under the URL field (first column) */}
                <div className="mt-4 md:mt-2 flex">
                  <button
                    onClick={() => handleTestConnection(env.key)}
                    className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
                      isDarkMode
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-purple-500 hover:bg-purple-600 text-white'
                    }`}
                  >
                    Test Connection
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* ─────────────────────────────────────────────────────────────────
              F) “Save All” Button (remains, if you still want it)
          ───────────────────────────────────────────────────────────────── */}
          <div className="mt-8">
            <button
              onClick={handleSaveAll}
              className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
      isDarkMode
        ? 'bg-purple-600 hover:bg-purple-700 text-white'
        : 'bg-purple-500 hover:bg-purple-600 text-white'
              }`}
            >
              Save All
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
